import React, { useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useCompanies } from './hooks';
import { useCurrentDomain } from './context/DomainContext';
import { useDoctors } from './hooks';
import { manageSupervisingDoctor } from './services/masterDataService';

const SupervisingDoctorConfiguration = () => {
    const { domain } = useCurrentDomain();
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedSupervisingDoctor, setSelectedSupervisingDoctor] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [addLoading, setAddLoading] = useState(false);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [showInfoMessage, setShowInfoMessage] = useState(false);
    const { companies, loading: companiesLoading } = useCompanies(domain);
    const { doctors, loading: doctorsLoading } = useDoctors(selectedCompany?.value, domain);

    const BRAND_GREEN = '#0aa375';

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '30px',
            fontSize: '12px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: BRAND_GREEN },
        }),
        indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
        dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
        clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
        option: (base, state) => ({
            ...base,
            fontSize: '12px',
            backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
            color: state.isSelected ? 'white' : '#333',
        }),
        singleValue: (base) => ({ ...base, fontSize: '12px' }),
        placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
    };

    const Field = ({ label, required }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                {label}
                {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
            </span>
        </div>
    );

    const handleCompanyChange = (selected) => {
        setSelectedCompany(selected);
        setSelectedSupervisingDoctor(null);
        setSelectedDoctor(null);
    };

    const handleAddSupervision = async () => {
        if (!selectedDoctor || !selectedSupervisingDoctor) return;
        setAddLoading(true);
        const loadingToast = toast.loading('Assigning supervising doctor...');
        try {
            await manageSupervisingDoctor(
                selectedDoctor.doctorId ?? selectedDoctor.value,
                selectedSupervisingDoctor.value,
                domain
            );
            toast.dismiss(loadingToast);
            toast.success('Supervising doctor assigned successfully.');
            setShowInfoMessage(true);
            setSelectedSupervisingDoctor(null);
            setSelectedDoctor(null);
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to assign supervising doctor.');
        } finally {
            setAddLoading(false);
        }
    };

    const handleRemoveSupervision = () => {
        if (!selectedDoctor) return;
        const toastId = toast(
            <div style={{ textAlign: 'center', minWidth: '250px' }}>
                <div style={{ fontWeight: '600', marginBottom: '10px' }}>
                    Remove supervising doctor for <br />
                    <span style={{ color: BRAND_GREEN }}>{selectedDoctor.label}</span>?
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => toast.dismiss(toastId)}>
                        Cancel
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => { toast.dismiss(toastId); performRemove(); }}>
                        Yes, Remove
                    </button>
                </div>
            </div>,
            { autoClose: false, closeButton: false, closeOnClick: false, draggable: false, className: 'bg-white shadow' }
        );
    };

    const performRemove = async () => {
        setRemoveLoading(true);
        const loadingToast = toast.loading('Removing supervising doctor...');
        try {
            await manageSupervisingDoctor(selectedDoctor.doctorId ?? selectedDoctor.value, null, domain);
            toast.dismiss(loadingToast);
            toast.success('Supervising doctor removed successfully.');
            setSelectedSupervisingDoctor(null);
            setSelectedDoctor(null);
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to remove supervising doctor.');
        } finally {
            setRemoveLoading(false);
        }
    };

    const handleReset = () => {
        setSelectedCompany(null);
        setSelectedSupervisingDoctor(null);
        setSelectedDoctor(null);
    };

const selectedDoctorId = selectedDoctor?.doctorId ?? selectedDoctor?.value;
const doctorOptions = doctors
    ?.filter(d => !d.isSupervisingDoctor && d.value !== selectedSupervisingDoctor?.value)
    ?.flatMap(d => {
        const users = d.users?.length ? d.users : [null];
        return users.map(u => ({
            ...d,
            doctorId: d.value,
            value: u ? `${d.value}::${u.userId}` : d.value,
            user: u || null,
            label: u
                ? `${d.label} | ${u.userName || '(no name)'} - ${u.userId}`
                : d.label,
        }));
    });
const supervisingDoctorOptions = doctors?.filter(d => d.isSupervisingDoctor && d.value !== selectedDoctorId);

    return (
        <div className="container-fluid p-0 px-1">
            <div className="card-body">
                <div className="card border" style={{ borderRadius: '6px' }}>
                    <div className="card-body p-2">
                        <div className="row g-2">
                            <div className="col-12 col-md-12">
                                <Field label="Company" required />
                                <Select
                                    options={companies}
                                    value={selectedCompany}
                                    onChange={handleCompanyChange}
                                    isLoading={companiesLoading}
                                    placeholder="Select company..."
                                    isSearchable
                                    isClearable
                                    styles={selectStyles}
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <Field label="Supervising Doctor List" required />
                                <Select
                                    options={supervisingDoctorOptions}
                                    value={selectedSupervisingDoctor}
                                    onChange={setSelectedSupervisingDoctor}
                                    isLoading={doctorsLoading}
                                    placeholder={!selectedCompany ? 'Select company first...' : 'Select supervising doctor...'}
                                    isSearchable
                                    isClearable
                                    isDisabled={!selectedCompany}
                                    styles={selectStyles}
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <Field label="Doctor List" required />
                                <Select
                                    options={doctorOptions}
                                    value={selectedDoctor}
                                    onChange={setSelectedDoctor}
                                    isLoading={doctorsLoading}
                                    placeholder={!selectedCompany ? 'Select company first...' : 'Select doctor...'}
                                    isSearchable
                                    isClearable
                                    isDisabled={!selectedCompany}
                                    styles={selectStyles}
                                />
                            </div>
                        </div>
                        {selectedSupervisingDoctor && selectedDoctor && (
                            <div className="mt-2 px-1 py-2" style={{ background: '#e6f7f2', borderRadius: '4px', border: '1px solid #0aa37522' }}>
                                <span style={{ fontSize: '11px', color: BRAND_GREEN, fontWeight: 600 }}>
                                    ✓ &nbsp;
                                    <span style={{ color: '#333' }}>{selectedSupervisingDoctor.label}</span>
                                    <span style={{ color: '#6c757d' }}> will supervise </span>
                                    <span style={{ color: '#333' }}>{selectedDoctor.label}</span>
                                </span>
                            </div>
                        )}
                        <div className="d-flex justify-content-center gap-2 mt-2">
                            <button
                                onClick={handleReset}
                                className="btn btn-outline-secondary btn-sm px-3"
                                style={{ fontSize: '12px' }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={handleRemoveSupervision}
                                disabled={removeLoading || !selectedDoctor}
                                className="btn btn-sm btn-danger px-3"
                                style={{ fontSize: '12px', opacity: (!selectedDoctor || removeLoading) ? 0.65 : 1 }}
                            >
                                {removeLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        Removing...
                                    </>
                                ) : (
                                    'Remove Supervising Doctor'
                                )}
                            </button>
                            <button
                                onClick={handleAddSupervision}
                                disabled={addLoading || !selectedDoctor || !selectedSupervisingDoctor}
                                className="btn btn-sm text-white px-3 fw-semibold"
                                style={{ backgroundColor: BRAND_GREEN, border: 'none', fontSize: '12px', opacity: (addLoading || !selectedDoctor || !selectedSupervisingDoctor) ? 0.65 : 1 }}
                            >
                                {addLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        Saving...
                                    </>
                                ) : (
                                    'Add Supervising Doctor'
                                )}
                            </button>
                        </div>

                    </div>
                </div>
                {showInfoMessage && (
                    <div className="mt-2 p-2" style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '4px' }}>
                        <span style={{ fontSize: '11px', color: '#856404', fontWeight: 500 }}>
                            Go to the doctor master of the doctor who has to be set as supervising physician and tick the supervising doctor checkbox.
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupervisingDoctorConfiguration;